import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import './OrderTracking.css';

const OrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'orders', orderId),
      (doc) => {
        if (doc.exists()) {
          setOrder({ id: doc.id, ...doc.data() });
        } else {
          alert('Order not found');
          navigate('/qr');
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error tracking order:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orderId]);

  const getStatusSteps = () => {
    const steps = [
      { key: 'pending', label: 'Order Received', icon: '📝' },
      { key: 'confirmed', label: 'Confirmed', icon: '✅' },
      { key: 'preparing', label: 'Preparing', icon: '🍳' },
      { key: 'ready', label: 'Ready to Serve', icon: '🔔' },
      { key: 'completed', label: 'Served', icon: '🍽️' }
    ];

    return steps;
  };

  const getCurrentStepIndex = () => {
    if (!order) return 0;
    const steps = getStatusSteps();
    const currentIndex = steps.findIndex(step => step.key === order.status);
    return currentIndex >= 0 ? currentIndex : 0;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-large"></div>
        <p>Loading order status...</p>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const steps = getStatusSteps();
  const currentIndex = getCurrentStepIndex();

  return (
    <div className="order-tracking-page">
      <div className="tracking-header">
        <button className="back-btn" onClick={() => navigate('/qr')}>
          ← Back
        </button>
        <h1>Track Your Order</h1>
        <p>Order #{order.orderNumber || order.id.slice(-6)}</p>
      </div>

      <div className="tracking-content">
        <div className="order-status-card">
          <div className="current-status">
            <div className="status-icon">{steps[currentIndex]?.icon}</div>
            <h2>{steps[currentIndex]?.label}</h2>
            {order.status === 'preparing' && (
              <p className="status-message">Your delicious food is being prepared! 🍳</p>
            )}
            {order.status === 'ready' && (
              <p className="status-message">Your order is ready! Our staff will serve it soon. 🔔</p>
            )}
            {order.status === 'completed' && (
              <p className="status-message">Enjoy your meal! 😊</p>
            )}
          </div>

          <div className="status-timeline">
            {steps.map((step, index) => (
              <div 
                key={step.key}
                className={`timeline-step ${index <= currentIndex ? 'completed' : ''} ${index === currentIndex ? 'current' : ''}`}
              >
                <div className="step-marker">
                  {index < currentIndex ? '✓' : step.icon}
                </div>
                <div className="step-content">
                  <strong>{step.label}</strong>
                  {index === currentIndex && (
                    <span className="step-badge">Current</span>
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`step-connector ${index < currentIndex ? 'completed' : ''}`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="order-info-card">
          <h3>Order Details</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Table</span>
              <span className="info-value">Table {order.tableNumber}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Items</span>
              <span className="info-value">{order.items?.length || 0} items</span>
            </div>
            <div className="info-item">
              <span className="info-label">Total</span>
              <span className="info-value">₹{order.total?.toFixed(2)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Payment</span>
              <span className="info-value capitalize">{order.paymentMethod}</span>
            </div>
          </div>

          <div className="items-list">
            <h4>Items Ordered:</h4>
            {order.items?.map((item, index) => (
              <div key={index} className="tracking-item">
                <span>{item.quantity}x {item.name}</span>
                <span>₹{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="help-card">
          <h4>Need Assistance?</h4>
          <p>If you have any questions or need help, please call a waiter or contact our staff.</p>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
